import { isNotEmptyString } from './string';
import { padZeros } from './number';

export const LOG_MESSAGE_DELIMITER = ' :: ';
export const CONSOLE_LOG_TIMESTAMP_REG_EX = /^\[\d{2}:\d{2}:\d{2}\.\d{3}]/;

const LOG_TRACE = false;
const LOG_DEBUG = false;

enum LogLevel {
  TRACE = 'trace',
  DEBUG = 'debug',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error',
}

/**
 * Logs the given message as a standard informational message.  This is a synonym for {@link logInfo}.
 *
 * @param message the message to log.
 * @param optionalParameters additional context objects to report with the message.
 */
export function logMessage(message: string, ...optionalParameters: any[]): void {
  logInfo(message, optionalParameters);
}

/**
 * Logs the given message, with a possible exception trace, as an error.
 *
 * @param message the message to log.
 * @param error an error object cause to associate with the message.
 * @param optionalParameters additional context objects to report with the message.
 */
export function logError(message: string, error?: any, ...optionalParameters: any[]): void {
  logMessageInternal(LogLevel.ERROR, message, error, optionalParameters);
}

/**
 * Logs the given message as a warning.
 *
 * @param message the message to log.
 * @param optionalParameters additional context objects to report with the message.
 */
export function logWarn(message: string, ...optionalParameters: any[]): void {
  logMessageInternal(LogLevel.WARN, message, optionalParameters);
}

/**
 * Logs a given message to the console.
 *
 * @param message the message to log.
 * @param optionalParameters additional context objects to report with the message.
 */
export function logInfo(message: string, ...optionalParameters: any[]): void {
  logMessageInternal(LogLevel.INFO, message, optionalParameters);
}

/**
 * Logs a given message to the console if the debug flag has been enabled.
 *
 * @param message the message to log.
 * @param optionalParameters additional context objects to report with the message.
 */
export function logDebug(message: string, ...optionalParameters: any[]): void {
  if (LOG_DEBUG) {
    logMessageInternal(LogLevel.DEBUG, message, optionalParameters);
  }
}

/**
 * Logs a given message to the console if the trace flag has been enabled.
 *
 * @param message the message to log.
 * @param optionalParameters additional context objects to report with the message.
 */
export function logTrace(message: string, ...optionalParameters: any[]): void {
  if (LOG_TRACE) {
    logMessageInternal(LogLevel.TRACE, message, optionalParameters);
  }
}

/**
 * Logs a given message to the console if the trace flag has been enabled.
 *
 * @param component the component identifier to trace.
 * @param action optional action to identify what was being done in the component.
 * @param detail optional detail to add to the trace message.
 * @param optionalParameters additional context objects to report with the message.
 */
export function logTraceComponent(
  component: string,
  action?: string,
  detail?: string,
  ...optionalParameters: any[]
): void {
  if (LOG_TRACE) {
    let message = component;

    if (action && action.length > 0) {
      message = message.concat(LOG_MESSAGE_DELIMITER).concat(action);
    }

    if (detail && detail.length > 0) {
      message = message.concat(LOG_MESSAGE_DELIMITER).concat(detail);
    }

    logTrace(message, optionalParameters);
  }
}

/**
 * Type checks a given error object to determine the best way to extract a message from it and returns that message
 * as a string.
 *
 * @param error the error to parse.
 *
 * @return the error message pulled from the error object or a default 'Unknown' or 'Undefined'.
 */
export function getErrorMessage(error: any): string {
  if (error) {
    if (isNotEmptyString(error.message)) {
      // Reminder that if this is a NiceApiError type, it runs the getter function...
      return error.message;
    }
    if (isNotEmptyString(error.error)) {
      return error.error;
    }
    if (isNotEmptyString(error.msg)) {
      return error.msg;
    }
    return 'Unknown';
  }
  return 'Undefined';
}

function logMessageInternal(logLevel: LogLevel, message: string, ...optionalParameters: any[]): void {
  const optionalParametersObject = getOptionalParametersAsObject(optionalParameters);

  if (optionalParametersObject) {
    switch (logLevel) {
      case LogLevel.TRACE:
        // Note console.trace doesn't show up in the console unless the user has explicitly allowed it.
        // eslint-disable-next-line no-console
        console.info(prependTimestamp(message), optionalParametersObject);
        // TODO datadogLogs.logger.debug(message, optionalParametersObject);
        break;
      case LogLevel.DEBUG:
        // Note console.debug doesn't show up in the console unless the user has explicitly allowed it.
        // eslint-disable-next-line no-console
        console.info(prependTimestamp(message), optionalParametersObject);
        // TODO datadogLogs.logger.debug(message, optionalParametersObject);
        break;
      case LogLevel.INFO:
        // Note that console.info is just an alias for console.log.
        // eslint-disable-next-line no-console
        console.info(prependTimestamp(message), optionalParametersObject);
        // TODO datadogLogs.logger.info(message, optionalParametersObject);
        break;
      case LogLevel.WARN:
        // eslint-disable-next-line no-console
        console.warn(prependTimestamp(message), optionalParametersObject);
        // TODO datadogLogs.logger.warn(message, optionalParametersObject);
        break;
      case LogLevel.ERROR:
        // eslint-disable-next-line no-console
        console.error(prependTimestamp(message), optionalParametersObject);
        // TODO datadogLogs.logger.error(message, optionalParametersObject);
        break;
      default:
        // eslint-disable-next-line no-console
        console.error(`Unknown log level: ${logLevel}`);
        // TODO datadogLogs.logger.error(`Unknown log level: ${logLevel}`);
        // eslint-disable-next-line no-console
        console.info(prependTimestamp(message), optionalParametersObject);
        // TODO datadogLogs.logger.info(message, optionalParametersObject);
        break;
    }
  } else {
    switch (logLevel) {
      case LogLevel.TRACE:
        // Note console.trace doesn't show up in the console unless the user has explicitly allowed it.
        // eslint-disable-next-line no-console
        console.info(prependTimestamp(message));
        // TODO datadogLogs.logger.debug(message, optionalParametersObject);
        break;
      case LogLevel.DEBUG:
        // Note console.debug doesn't show up in the console unless the user has explicitly allowed it.
        // eslint-disable-next-line no-console
        console.info(prependTimestamp(message));
        // TODO datadogLogs.logger.debug(message);
        break;
      case LogLevel.INFO:
        // Note that console.info is just an alias for console.log.
        // eslint-disable-next-line no-console
        console.info(prependTimestamp(message));
        // TODO datadogLogs.logger.info(message);
        break;
      case LogLevel.WARN:
        // eslint-disable-next-line no-console
        console.warn(prependTimestamp(message));
        // TODO datadogLogs.logger.warn(message);
        break;
      case LogLevel.ERROR:
        // eslint-disable-next-line no-console
        console.error(prependTimestamp(message));
        // TODO datadogLogs.logger.error(message);
        break;
      default:
        // eslint-disable-next-line no-console
        console.error(`Unknown log level: ${logLevel}`);
        // TODO datadogLogs.logger.error(`Unknown log level: ${logLevel}`);
        // eslint-disable-next-line no-console
        console.info(prependTimestamp(message));
        // TODO datadogLogs.logger.info(message);
        break;
    }
  }
}

function prependTimestamp(messageToAddTimestamp: string | null | undefined): string {
  const now = new Date();
  return `[${padZeros(now.getHours())}:${padZeros(now.getMinutes())}:${padZeros(now.getSeconds())}.${padZeros(
    now.getMilliseconds(),
    3
  )}]  ${messageToAddTimestamp}`;
}

/**
 * Each time we use the "rest parameters" syntax it nests in an array
 * ({@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Functions/rest_parameters}).  This
 * method unwraps that nesting into an object for logging.
 *
 * @param optionalParameters the parameters to unwrap, if any.
 *
 * @return the unwrapped parameters or <code>undefined</code> if not parameters exist.
 */
function getOptionalParametersAsObject(...optionalParameters: any[]): any | undefined {
  let optionalParametersObject: any = null;
  let optionalParameterIterator: any = optionalParameters;
  let optionalParameterPreviousObject: any;

  while (
    optionalParameterIterator &&
    Array.isArray(optionalParameterIterator) &&
    optionalParameterIterator.length > 0
  ) {
    optionalParameterPreviousObject = optionalParametersObject;
    // eslint-disable-next-line prefer-destructuring
    optionalParameterIterator = optionalParameterIterator[0];
    optionalParametersObject = optionalParameterIterator;
  }

  if (Array.isArray(optionalParametersObject)) {
    // The nesting was just from "rest parameters".  No values were actually passed in.
    return undefined;
  }
  if (Array.isArray(optionalParameterPreviousObject)) {
    return {
      context: flattenArray(optionalParameterPreviousObject),
    };
  }
  return {
    context: optionalParametersObject,
  };

  /**
   * Converts an array of values into a standard object.  This currently only flattens one level deep.
   *
   * @param array the array to flatten.
   *
   * @return an object holding the values from the flattened array.
   */
  function flattenArray(array: any[]): any {
    const flattenedArray: any = {};

    if (array && Array.isArray(array)) {
      for (let i = 0; i < array.length; i++) {
        if (typeof array[i] === 'number' || typeof array[i] === 'string') {
          flattenedArray[i] = array[i];
        } else if (Array.isArray(array[i])) {
          // Skip if not populated.
          if (array[i].length > 0) {
            flattenedArray[`logContextArray${i}`] = array[i];
          }
        } else if (typeof array[i] === 'object' && array[i] !== null) {
          flattenedArray[`logContextValue${i}`] = array[i];
        } else if (array[i] !== null && array[i] !== undefined) {
          logWarn(`Unknown context value: ${JSON.stringify(array[i])}`);
        }
      }
    } else if (array) {
      logWarn(`Context is not an array: ${JSON.stringify(array)}`);
    }

    return flattenedArray;
  }
}
